import React, { useEffect, useState, useMemo, useRef } from "react";
import {
  Box,
  Flex,
  Heading,
  Text,
  useColorModeValue,
  Spinner,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Avatar,
  Badge,
  Button,
  HStack,
  TableContainer,
  Select,
  Input,
  VStack,
  Checkbox,
  CheckboxGroup,
  Tag,
  TagLabel,
  TagCloseButton,
  Wrap,
  WrapItem,
  InputGroup,
  InputLeftElement,
  Switch,
  IconButton,
  Tooltip,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  Textarea,
  useToast,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  SimpleGrid,
  Divider,
  Image,
} from "@chakra-ui/react";
import { ChevronLeftIcon, ChevronRightIcon, SearchIcon, EditIcon, DeleteIcon } from "@chakra-ui/icons";
import Card from "components/Card/Card";
import CardBody from "components/Card/CardBody";
import CardHeader from "components/Card/CardHeader";
import { productsService, API_CONFIG } from "api";
import { getUserData } from "../../utils/auth";

export default function Products() {
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingPagination, setIsLoadingPagination] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [products, setProducts] = useState([]);
  const [pagination, setPagination] = useState({ current_page: 1, per_page: 15, total: 0, last_page: 1 });

  const [filters, setFilters] = useState({
    search: "",
    companies: [],
    sub_categories: [],
    colors: [],
    genders: [],
    min_price: "",
    max_price: "",
    is_on_sale: null,
    sort_by: "",
    sort_direction: "",
    sizes: [],
  });
  const [enforcedCompanyId, setEnforcedCompanyId] = useState(null);
  const [filtersMeta, setFiltersMeta] = useState({ genders: [], colors: [], sizes: [], companies: [], sub_categories: [], price_range: { min_price: 0, max_price: 0 } });
  const [isLoadingFilters, setIsLoadingFilters] = useState(false);
  const toast = useToast();

  // Actions state
  const { isOpen: isDetailsOpen, onOpen: onDetailsOpen, onClose: onDetailsClose } = useDisclosure();
  const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure();
  const { isOpen: isSaleOpen, onOpen: onSaleOpen, onClose: onSaleClose } = useDisclosure();
  const { isOpen: isSaleEditOpen, onOpen: onSaleEditOpen, onClose: onSaleEditClose } = useDisclosure();
  const { isOpen: isSaleDeleteOpen, onOpen: onSaleDeleteOpen, onClose: onSaleDeleteClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedSale, setSelectedSale] = useState(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [actionLoading, setActionLoading] = useState({ update: false, delete: false, sale: false, saleUpdate: false, saleDelete: false });
  const [saleEditForm, setSaleEditForm] = useState({ discount_type: "percentage", discount_amount: 0, start_date: "", end_date: "" });

  // Edit form state
  const [editForm, setEditForm] = useState({ name: "", description: "", gender: "unisex", is_on_sale: false, points: 0 });
  const [editFlags, setEditFlags] = useState({ is_replaceable: true, is_refundable: true });
  const [editCompanyId, setEditCompanyId] = useState("");
  const [editCategories, setEditCategories] = useState([]);
  const [editBranches, setEditBranches] = useState([]);
  const [editImages, setEditImages] = useState([]);
  const [imageItems, setImageItems] = useState([]); // unified list: { kind: 'existing'|'new', url, file? }
  const [originalExistingImages, setOriginalExistingImages] = useState([]); // original existing urls
  const [deletedImages, setDeletedImages] = useState([]); // urls to delete
  const dragIndexRef = useRef(null);
  const fileInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [companyBranches, setCompanyBranches] = useState([]);
  const [selectedProductIds, setSelectedProductIds] = useState([]);

  const cardBg = useColorModeValue("white", "navy.700");
  const textColor = useColorModeValue("gray.700", "white");
  const borderColor = useColorModeValue("gray.200", "gray.600");

  const fetchPage = async (page = 1, perPageOverride) => {
    setIsLoading(true);
    setApiError(null);
    try {
      const perPageToUse = perPageOverride ?? pagination.per_page;
      const params = {
        page,
        per_page: perPageToUse,
        ...filters,
      };
      // If a company is enforced, add it to params and hide from UI separately
      if (enforcedCompanyId && (!params.companies || params.companies.length === 0)) {
        params.companies = [String(enforcedCompanyId)];
      }
      // normalize booleans and empty values
      if (params.is_on_sale === null || params.is_on_sale === "") delete params.is_on_sale;
      if (!params.search?.trim()) delete params.search;

      const res = await productsService.getProducts(params);
      const ok = res?.success === true && (res?.data?.status === 1 || res?.status === 200);
      const payload = res?.data?.data || res?.data;

      if (ok && payload) {
        const items = Array.isArray(payload?.data) ? payload.data : Array.isArray(payload) ? payload : [];
        setProducts(items);
        setPagination({
          current_page: payload?.current_page || page,
          per_page: payload?.per_page || perPageToUse,
          total: payload?.total ?? items.length,
          last_page: payload?.last_page || 1,
        });
      } else {
        setApiError(res?.data?.message || res?.message || "Failed to load products");
      }
    } catch (e) {
      setApiError(e?.message || "Failed to load products");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const loadFilters = async () => {
      setIsLoadingFilters(true);
      try {
        const resp = await productsService.getFilters();
        const data = resp?.data?.data || resp?.data || {};
        setFiltersMeta({
          genders: data.genders || [],
          colors: data.colors || [],
          sizes: data.sizes || [],
          companies: data.companies || [],
          sub_categories: data.sub_categories || [],
          price_range: data.price_range || { min_price: 0, max_price: 0 },
        });
        // Initialize price filters to API range
        if (data.price_range) {
          setFilters(prev => ({
            ...prev,
            min_price: data.price_range.min_price,
            max_price: data.price_range.max_price,
          }));
        }
        // Enforce company filter for company users
        try {
          const u = getUserData();
          // Detect company user by presence of logo or known company fields
          if (u && (u.logo || u.description || typeof u.user_id !== 'undefined')) {
            setEnforcedCompanyId(u.id);
            setFilters(prev => ({ ...prev, companies: [String(u.id)] }));
          }
        } catch {}
      } finally {
        setIsLoadingFilters(false);
      }
    };
    loadFilters();
    fetchPage(1);
  }, []);

  // Debounce filters
  useEffect(() => {
    const t = setTimeout(() => {
      setPagination(prev => ({ ...prev, current_page: 1 }));
      fetchPage(1);
    }, 500);
    return () => clearTimeout(t);
  }, [filters]);

  const handlePageChange = async (newPage) => {
    if (newPage >= 1 && newPage <= pagination.last_page) {
      setIsLoadingPagination(true);
      try {
        await fetchPage(newPage);
      } finally {
        setIsLoadingPagination(false);
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try { return new Date(dateString).toLocaleDateString('en-US'); } catch { return "-"; }
  };

  const areDatesInvalid = (start, end) => {
    if (!start || !end) return false;
    try {
      const s = new Date(start);
      const e = new Date(end);
      return !(s instanceof Date) || !(e instanceof Date) ? false : (s >= e);
    } catch {
      return false;
    }
  };

  const fromIndex = ((pagination.current_page - 1) * pagination.per_page) + 1;
  const toIndex = Math.min(pagination.current_page * pagination.per_page, pagination.total);

  const updateArrayFilter = (key, value, checked) => {
    setFilters(prev => {
      const current = new Set(prev[key] || []);
      if (checked) current.add(value); else current.delete(value);
      return { ...prev, [key]: Array.from(current) };
    });
  };

  const selectedChips = useMemo(() => {
    const getNameById = (list, id) => list.find(i => `${i.id}` === `${id}`)?.name || id;
    return [
      ...filters.companies.map(id => ({ key: `company-${id}`, group: 'companies', label: getNameById(filtersMeta.companies, id) })),
      ...filters.sub_categories.map(id => ({ key: `sub-${id}`, group: 'sub_categories', label: filtersMeta.sub_categories.find(s => `${s.id}` === `${id}`)?.en_name || id })),
      ...filters.colors.map(id => ({ key: `color-${id}`, group: 'colors', label: getNameById(filtersMeta.colors, id) })),
      ...filters.sizes.map(id => ({ key: `size-${id}`, group: 'sizes', label: getNameById(filtersMeta.sizes, id) })),
      ...filters.genders.map(g => ({ key: `gender-${g}`, group: 'genders', label: g })),
    ];
  }, [filters, filtersMeta]);

  // Reset create form when opening create modal
  useEffect(() => {
    if (isCreateOpen) {
      setSelectedProduct(null);
      setEditForm({ name: "", description: "", gender: "female", is_on_sale: false, points: 0 });
      setEditFlags({ is_replaceable: false, is_refundable: false });
      // Default to first company option if available
      const firstCompanyId = (filtersMeta.companies && filtersMeta.companies.length > 0) ? String(filtersMeta.companies[0].id) : "";
      setEditCompanyId(firstCompanyId);
      setEditCategories([]);
      setEditBranches([]);
      setImageItems([]);
    }
  }, [isCreateOpen, filtersMeta.companies]);

  // Load branches when company changes
  useEffect(() => {
    const loadBranches = async () => {
      try {
        if (!editCompanyId) { setCompanyBranches([]); return; }
        const resp = await productsService.getCompanyBranches(editCompanyId);
        const data = resp?.data?.data || resp?.data || [];
        setCompanyBranches(Array.isArray(data) ? data : []);
      } catch (e) {
        setCompanyBranches([]);
      }
    };
    loadBranches();
  }, [editCompanyId]);

  // Backfill empty branch_id with first available branch when branches list arrives
  useEffect(() => {
    if (!companyBranches || companyBranches.length === 0) return;
    setEditBranches(prev => prev.map(b => ({ ...b, branch_id: b.branch_id || companyBranches[0]?.id })));
  }, [companyBranches]);

  // Action handlers
  const openDetails = async (product) => {
    setSelectedProduct(null);
    setIsLoadingDetails(true);
    onDetailsOpen();
    try {
      const resp = await productsService.getProductById(product.id);
      const data = resp?.data?.data || resp?.data || null;
      setSelectedProduct(data);
    } catch (e) {
      toast({ title: "خطأ", description: "تعذر تحميل تفاصيل المنتج", status: "error", duration: 4000, isClosable: true });
    } finally {
      setIsLoadingDetails(false);
    }
  };

  const openEdit = async (product) => {
    onEditOpen();
    setIsLoadingDetails(true);
    try {
      const resp = await productsService.getProductById(product.id);
      const data = resp?.data?.data || resp?.data || product;
      setSelectedProduct(data);
      setEditForm({
        name: data?.name || "",
        description: data?.description || "",
        gender: data?.gender || "unisex",
        is_on_sale: !!data?.is_on_sale,
        points: data?.points || 0,
      });
      setEditFlags({
        is_replaceable: !!data?.is_replaceable,
        is_refundable: !!data?.is_refundable,
      });
      setEditCompanyId(data?.company_id || "");
      setEditCategories((data?.categories || []).map(c => c.id));
      // Transform existing branch_products to editable structure
      const branches = (data?.branch_products || []).map(bp => ({
        branch_id: bp.branch_id,
        price: bp.price,
        colors: (bp.product_storage || []).reduce((acc, ps) => {
          const existing = acc.find(c => c.color_id === (ps.color_id || ps.color?.id));
          const entry = { size_id: ps.size_id || ps.size?.id, quantity: ps.quantity };
          if (existing) existing.sizes.push(entry);
          else acc.push({ color_id: ps.color_id || ps.color?.id, sizes: [entry] });
          return acc;
        }, []),
      }));
      setEditBranches(branches);
      setEditImages([]);
      const initialExisting = (data?.images || []).map(im => im.url);
      setOriginalExistingImages(initialExisting);
      setImageItems(initialExisting.map(u => ({ kind: 'existing', url: u })));
      setDeletedImages([]);
    } catch (e) {
      toast({ title: "خطأ", description: "تعذر تحميل بيانات التعديل", status: "error", duration: 4000, isClosable: true });
    } finally {
      setIsLoadingDetails(false);
    }
  };

  const saveEdit = async () => {
    if (!selectedProduct) return;
    setActionLoading(prev => ({ ...prev, update: true }));
    try {
      // Keep existing values for required fields not edited here
      const remainingExisting = imageItems.filter(it => it.kind === 'existing').map(it => it.url);
      const newFilesInOrder = imageItems.filter(it => it.kind === 'new').map(it => it.file);

      // Fetch existing image URLs via API to Files
      const toApiImageUrl = (url) => {
        try {
          const match = url.match(/\/storage\/(\d+)\/([^?#]+)/);
          if (match) {
            const folder = match[1];
            const filename = match[2];
            return `${API_CONFIG.BASE_URL}/image/${folder}/${filename}`;
          }
          return url;
        } catch { return url; }
      };

      const existingAsFiles = await Promise.all(remainingExisting.map(async (url, idx) => {
        try {
          const apiUrl = toApiImageUrl(url);
          const res = await fetch(apiUrl);
          const blob = await res.blob();
          const ext = (blob.type && blob.type.split('/')[1]) || 'jpg';
          return new File([blob], `existing-${idx}.${ext}`, { type: blob.type || 'image/jpeg' });
        } catch {
          return null;
        }
      }));
      const finalImagesFiles = [...existingAsFiles.filter(Boolean), ...newFilesInOrder];
      const payload = {
        name: editForm.name,
        description: editForm.description,
        gender: editForm.gender,
        is_on_sale: !!editForm.is_on_sale,
        is_replaceable: !!editFlags.is_replaceable,
        is_refundable: !!editFlags.is_refundable,
        points: editForm.points || 0,
        company_id: editCompanyId || selectedProduct?.company_id,
        tags: selectedProduct?.tags || [],
        categories: editCategories,
        branches: editBranches,
        images: finalImagesFiles,
      };
      await productsService.updateProduct(selectedProduct.id, payload);
      toast({ title: "تم الحفظ", description: "تم تحديث المنتج", status: "success", duration: 3000, isClosable: true });
      onEditClose();
      fetchPage(pagination.current_page);
    } catch (e) {
      toast({ title: "خطأ", description: "تعذر حفظ التعديلات", status: "error", duration: 4000, isClosable: true });
    } finally {
      setActionLoading(prev => ({ ...prev, update: false }));
    }
  };

  const openSale = async (product) => {
    onSaleOpen();
    setIsLoadingDetails(true);
    try {
      const resp = await productsService.getProductById(product.id);
      let data = resp?.data?.data || resp?.data || product;
      if (data) {
        const listSales = Array.isArray(product?.sales) ? product.sales : [];
        const detailSales = Array.isArray(data?.sales) ? data.sales : [];
        // If detail lacks sales but list has them, merge them in
        if (detailSales.length === 0 && listSales.length > 0) {
          data = { ...data, sales: listSales };
        }
        // Ensure active_sale is preserved if missing from detail
        if (data.active_sale == null && product?.active_sale != null) {
          data = { ...data, active_sale: product.active_sale };
        }
      }
      setSelectedProduct(data);
    } catch (e) {
      setSelectedProduct(product);
    } finally {
      setIsLoadingDetails(false);
    }
  };

  const confirmAddSale = async () => {
    if (!selectedProduct) return;
    setActionLoading(prev => ({ ...prev, sale: true }));
    try {
      const base = selectedProduct;
      const payload = {
        name: base?.name,
        description: base?.description,
        gender: base?.gender,
        is_on_sale: true,
        is_replaceable: !!base?.is_replaceable,
        is_refundable: !!base?.is_refundable,
        points: base?.points || 0,
        company_id: base?.company_id,
        tags: base?.tags || [],
        categories: (base?.categories || []).map(c => c.id),
      };
      await productsService.updateProduct(base.id, payload);
      toast({ title: "تم التفعيل", description: "تم تفعيل التخفيض للمنتج", status: "success", duration: 3000, isClosable: true });
      onSaleClose();
      fetchPage(pagination.current_page);
    } catch (e) {
      toast({ title: "خطأ", description: "تعذر إضافة التخفيض", status: "error", duration: 4000, isClosable: true });
    } finally {
      setActionLoading(prev => ({ ...prev, sale: false }));
    }
  };

  const [deletingProduct, setDeletingProduct] = useState(null);
  const askDelete = (product) => { setDeletingProduct(product); onDeleteOpen(); };
  const confirmDelete = async () => {
    if (!deletingProduct) return;
    setActionLoading(prev => ({ ...prev, delete: true }));
    try {
      await productsService.deleteProduct(deletingProduct.id);
      toast({ title: "تم الحذف", description: "تم حذف المنتج", status: "success", duration: 3000, isClosable: true });
      onDeleteClose();
      setDeletingProduct(null);
      fetchPage(1);
    } catch (e) {
      toast({ title: "خطأ", description: "تعذر حذف المنتج", status: "error", duration: 4000, isClosable: true });
    } finally {
      setActionLoading(prev => ({ ...prev, delete: false }));
    }
  };

  // Sales actions
  const openSaleEdit = (sale) => {
    setSelectedSale(sale);
    const toDateInput = (d) => {
      try { return d ? new Date(d).toISOString().slice(0, 10) : ""; } catch { return ""; }
    };
    setSaleEditForm({
      discount_type: sale?.discount_type || "percentage",
      discount_amount: sale?.discount_amount ?? 0,
      start_date: toDateInput(sale?.start_date),
      end_date: toDateInput(sale?.end_date),
    });
    onSaleEditOpen();
  };

  const saveSaleEdit = async () => {
    if (!selectedProduct) return;
    if ((saleEditForm.discount_amount ?? 0) < 0) {
      toast({ title: "خطأ", description: "قيمة التخفيض لا يمكن أن تكون سالبة", status: "error", duration: 4000, isClosable: true });
      return;
    }
    if (areDatesInvalid(saleEditForm.start_date, saleEditForm.end_date)) {
      toast({ title: "خطأ", description: "تاريخ البداية يجب أن يكون قبل تاريخ النهاية", status: "error", duration: 4000, isClosable: true });
      return;
    }
    setActionLoading(prev => ({ ...prev, update: true }));
    try {
      // Keep existing values for required fields not edited here
      const remainingExisting = imageItems.filter(it => it.kind === 'existing').map(it => it.url);
      const newFilesInOrder = imageItems.filter(it => it.kind === 'new').map(it => it.file);

      // Fetch existing image URLs via API to Files
      const toApiImageUrl = (url) => {
        try {
          const match = url.match(/\/storage\/(\d+)\/([^?#]+)/);
          if (match) {
            const folder = match[1];
            const filename = match[2];
            return `${API_CONFIG.BASE_URL}/image/${folder}/${filename}`;
          }
          return url;
        } catch { return url; }
      };

      const existingAsFiles = await Promise.all(remainingExisting.map(async (url, idx) => {
        try {
          const apiUrl = toApiImageUrl(url);
          const res = await fetch(apiUrl);
          const blob = await res.blob();
          const ext = (blob.type && blob.type.split('/')[1]) || 'jpg';
          return new File([blob], `existing-${idx}.${ext}`, { type: blob.type || 'image/jpeg' });
        } catch {
          return null;
        }
      }));
      const finalImagesFiles = [...existingAsFiles.filter(Boolean), ...newFilesInOrder];
      const payload = {
        name: editForm.name,
        description: editForm.description,
        gender: editForm.gender,
        is_on_sale: !!editForm.is_on_sale,
        is_replaceable: !!editFlags.is_replaceable,
        is_refundable: !!editFlags.is_refundable,
        points: editForm.points || 0,
        company_id: editCompanyId || selectedProduct?.company_id,
        tags: selectedProduct?.tags || [],
        categories: editCategories,
        branches: editBranches,
        images: finalImagesFiles,
      };
      await productsService.updateProduct(selectedProduct.id, payload);
      toast({ title: "تم الحفظ", description: "تم تحديث المنتج", status: "success", duration: 3000, isClosable: true });
      onSaleEditClose();
      fetchPage(pagination.current_page);
    } catch (e) {
      toast({ title: "خطأ", description: "تعذر حفظ التعديلات", status: "error", duration: 4000, isClosable: true });
    } finally {
      setActionLoading(prev => ({ ...prev, update: false }));
    }
  };

  const askDeleteSale = (sale) => {
    setSelectedSale(sale);
    onSaleDeleteOpen();
  };

  const confirmDeleteSale = async () => {
    if (!selectedSale) return;
    setActionLoading(prev => ({ ...prev, saleDelete: true }));
    try {
      await productsService.deleteSale(selectedSale.id);
      setSelectedProduct(prev => {
        if (!prev) return prev;
        const remaining = (prev.sales || []).filter(s => s.id !== selectedSale.id);
        const newActive = remaining.find(s => s.active) || null;
        return { ...prev, sales: remaining, active_sale: newActive };
      });
      toast({ title: "تم الحذف", description: "تم حذف التخفيض", status: "success", duration: 3000, isClosable: true });
      onSaleDeleteClose();
      setSelectedSale(null);
    } catch (e) {
      toast({ title: "خطأ", description: "تعذر حذف التخفيض", status: "error", duration: 4000, isClosable: true });
    } finally {
      setActionLoading(prev => ({ ...prev, saleDelete: false }));
    }
  };

  const { isOpen: isBulkSaleOpen, onOpen: onBulkSaleOpen, onClose: onBulkSaleClose } = useDisclosure();
  const [bulkSaleForm, setBulkSaleForm] = useState({ discount_type: "percentage", discount_amount: 0, start_date: "", end_date: "" });

  const submitBulkSale = async () => {
    if (!selectedProductIds || selectedProductIds.length === 0) return;
    if ((bulkSaleForm.discount_amount ?? 0) < 0) {
      toast({ title: "خطأ", description: "قيمة التخفيض لا يمكن أن تكون سالبة", status: "error", duration: 4000, isClosable: true });
      return;
    }
    if (areDatesInvalid(bulkSaleForm.start_date, bulkSaleForm.end_date)) {
      toast({ title: "خطأ", description: "تاريخ البداية يجب أن يكون قبل تاريخ النهاية", status: "error", duration: 4000, isClosable: true });
      return;
    }
    setActionLoading(prev => ({ ...prev, saleUpdate: true }));
    try {
      await productsService.createProductSales({
        product_ids: selectedProductIds,
        discount_type: bulkSaleForm.discount_type,
        discount_amount: bulkSaleForm.discount_amount,
        start_date: bulkSaleForm.start_date || null,
        end_date: bulkSaleForm.end_date || null,
      });
      toast({ title: "تم الإضافة", description: "تم إنشاء التخفيض للمنتجات المحددة", status: "success", duration: 3000, isClosable: true });
      onBulkSaleClose();
      setSelectedProductIds([]);
      fetchPage(pagination.current_page);
    } catch (e) {
      toast({ title: "خطأ", description: "تعذر إنشاء التخفيض", status: "error", duration: 4000, isClosable: true });
    } finally {
      setActionLoading(prev => ({ ...prev, saleUpdate: false }));
    }
  };

  return (
    <Flex direction="column" pt={{ base: "120px", md: "75px" }}>
      <Card overflowX={{ sm: "scroll", xl: "hidden" }} pb="0px" bg={cardBg}>
        <CardHeader p="6px 0px 22px 0px">
          <Flex align="center" justify="space-between" w="100%">
            <Heading size="md" color={textColor}>المنتجات</Heading>
            <HStack>
              <Button colorScheme="blue" size="sm" onClick={() => setIsCreateOpen(true)}>إضافة منتج</Button>
              {selectedProductIds.length > 0 && (
                <Button colorScheme="purple" size="sm" variant="solid" onClick={() => onBulkSaleOpen()}>إضافة تخفيض</Button>
              )}
            </HStack>
          </Flex>
        </CardHeader>

        <CardBody>
          {/* Filters Row */}
          <VStack align="stretch" spacing={4} mb={4}>
            <Flex gap={3} wrap="wrap" align="center">
              <InputGroup w={{ base: "100%", md: "260px" }}>
                <InputLeftElement pointerEvents="none">
                  <SearchIcon color="gray.400" />
                </InputLeftElement>
                <Input
                  placeholder="بحث..."
                  value={filters.search}
                  onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                  borderColor={borderColor}
                />
              </InputGroup>

              {!enforcedCompanyId && (
                <Select
                  placeholder={isLoadingFilters ? "تحميل الشركات..." : "الشركات"}
                  w={{ base: "100%", md: "220px" }}
                  value=""
                  onChange={(e) => {
                    if (!e.target.value) return;
                    updateArrayFilter('companies', e.target.value, true);
                  }}
                >
                  {(filtersMeta.companies || []).map(c => (
                    <option key={c.id} value={c.id}>{c.id} - {c.name}</option>
                  ))}
                </Select>
              )}

              <Select
                placeholder={isLoadingFilters ? "تحميل الفئات..." : "الفئات الفرعية"}
                w={{ base: "100%", md: "220px" }}
                value=""
                onChange={(e) => { if (e.target.value) updateArrayFilter('sub_categories', e.target.value, true); }}
              >
                {(filtersMeta.sub_categories || []).map(s => (
                  <option key={s.id} value={s.id}>{s.id} - {(s.en_name || s.name)}</option>
                ))}
              </Select>

              <Select
                placeholder={isLoadingFilters ? "تحميل الألوان..." : "الألوان"}
                w={{ base: "100%", md: "200px" }}
                value=""
                onChange={(e) => { if (e.target.value) updateArrayFilter('colors', e.target.value, true); }}
              >
                {(filtersMeta.colors || []).map(col => (
                  <option key={col.id} value={col.id}>{col.name}</option>
                ))}
              </Select>

              <Select
                placeholder={isLoadingFilters ? "تحميل المقاسات..." : "المقاسات"}
                w={{ base: "100%", md: "200px" }}
                value=""
                onChange={(e) => { if (e.target.value) updateArrayFilter('sizes', e.target.value, true); }}
              >
                {(filtersMeta.sizes || []).map(sz => (
                  <option key={sz.id} value={sz.id}>{sz.name || sz.id}</option>
                ))}
              </Select>

              <CheckboxGroup
                value={filters.genders}
                onChange={(vals) => setFilters(prev => ({ ...prev, genders: vals }))}
              >
                <HStack>
                  {(filtersMeta.genders || []).map(g => (
                    <Checkbox key={g} value={g}>{g}</Checkbox>
                  ))}
                </HStack>
              </CheckboxGroup>

              <HStack>
                <Text fontSize="sm">سعر من</Text>
                <Input
                  type="number"
                  w="100px"
                  step="0.01"
                  min={Math.max(0, filtersMeta.price_range?.min_price || 0)}
                  max={filtersMeta.price_range?.max_price || undefined}
                  value={filters.min_price}
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (raw === "") { setFilters(prev => ({ ...prev, min_price: "" })); return; }
                    let num = parseFloat(raw);
                    if (isNaN(num)) return;
                    const minBound = Math.max(0, filtersMeta.price_range?.min_price || 0);
                    const maxBound = filtersMeta.price_range?.max_price || Number.MAX_VALUE;
                    num = Math.min(Math.max(num, minBound), maxBound);
                    setFilters(prev => ({ ...prev, min_price: num }));
                  }}
                />
                <Text fontSize="sm">إلى</Text>
                <Input
                  type="number"
                  w="100px"
                  step="0.01"
                  min={Math.max(0, filtersMeta.price_range?.min_price || 0)}
                  max={filtersMeta.price_range?.max_price || undefined}
                  value={filters.max_price}
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (raw === "") { setFilters(prev => ({ ...prev, max_price: "" })); return; }
                    let num = parseFloat(raw);
                    if (isNaN(num)) return;
                    const minBound = Math.max(0, filtersMeta.price_range?.min_price || 0);
                    const maxBound = filtersMeta.price_range?.max_price || Number.MAX_VALUE;
                    num = Math.min(Math.max(num, minBound), maxBound);
                    setFilters(prev => ({ ...prev, max_price: num }));
                  }}
                />
              </HStack>

              <HStack>
                <Text fontSize="sm">تخفيض</Text>
                <Switch
                  isChecked={filters.is_on_sale === true}
                  onChange={(e) => setFilters(prev => ({ ...prev, is_on_sale: e.target.checked ? true : null }))}
                />
              </HStack>

              <HStack>
                <Select placeholder="ترتيب حسب" w="150px" value={filters.sort_by}
                  onChange={(e) => setFilters(prev => ({ ...prev, sort_by: e.target.value }))}
                >
                  <option value="name">الاسم</option>
                  <option value="created_at">تاريخ الإنشاء</option>
                </Select>
                <Select placeholder="الاتجاه" w="120px" value={filters.sort_direction}
                  onChange={(e) => setFilters(prev => ({ ...prev, sort_direction: e.target.value }))}
                >
                  <option value="asc">تصاعدي</option>
                  <option value="desc">تنازلي</option>
                </Select>
              </HStack>

              <Button size="sm" variant="outline" onClick={() => setFilters({
                search: "",
                companies: [], sub_categories: [], colors: [], genders: [], min_price: "", max_price: "", is_on_sale: null,
                sort_by: "", sort_direction: "", sizes: [],
              })}>مسح الفلاتر</Button>
            </Flex>

            {/* Selected chips */}
            <Wrap shouldWrapChildren>
              {selectedChips.map(ch => (
                <WrapItem key={ch.key}>
                  <Tag size="md" borderRadius="full" variant="subtle" colorScheme="gray">
                    <TagLabel>{ch.label}</TagLabel>
                    <TagCloseButton onClick={() => updateArrayFilter(ch.group, ch.label === 'male' || ch.label === 'female' || ch.label === 'unisex' ? ch.label : (ch.key.split('-')[1]), false)} />
                  </Tag>
                </WrapItem>
              ))}
            </Wrap>
          </VStack>

          {isLoading ? (
            <Flex align="center" justify="center" py={10} gap={3}>
              <Spinner />
              <Text color={textColor}>جاري تحميل المنتجات...</Text>
            </Flex>
          ) : apiError ? (
            <Text color="red.500">{apiError}</Text>
          ) : (
            <>
              <TableContainer>
                <Table variant="simple" color={textColor}>
                  <Thead>
                    <Tr>
                      <Th pl="0px" color="gray.400" borderColor={borderColor}>
                        <Checkbox
                          isChecked={Array.isArray(products) && products.length > 0 && selectedProductIds.length === products.length}
                          isIndeterminate={selectedProductIds.length > 0 && selectedProductIds.length < products.length}
                          onChange={(e) => {
                            if (e.target.checked) setSelectedProductIds((products || []).map(pp => pp.id));
                            else setSelectedProductIds([]);
                          }}
                        />
                      </Th>
                      <Th pl="0px" color="gray.400" borderColor={borderColor}>الصورة</Th>
                      <Th color="gray.400" borderColor={borderColor}>الاسم</Th>
                      <Th color="gray.400" borderColor={borderColor}>الشركة</Th>
                      <Th color="gray.400" borderColor={borderColor}>الجنس</Th>
                      <Th color="gray.400" borderColor={borderColor}>الفئات</Th>
                      <Th color="gray.400" borderColor={borderColor}>التخفيض</Th>
                      <Th color="gray.400" borderColor={borderColor}>التاريخ</Th>
                     <Th color="gray.400" borderColor={borderColor}>الإجراءات</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {products.map((p, idx) => {
                      const firstImage = p?.images?.[0]?.url || undefined;
                      const companyName = p?.company?.name || "-";
                      const categories = (p?.categories || []).map(c => c?.en_name || c?.name).filter(Boolean).join(", ");
                      const hasActiveSale = p?.active_sale != null;
                      const saleText = hasActiveSale ? "نعم" : "لا";
                      const createdAt = (p?.created_at ? formatDate(p.created_at) : "-");
                      const gender = p?.gender || "-";
                      const hasSales = Array.isArray(p?.sales) && p.sales.length > 0;
                      const isSaleActive = !!(p?.active_sale?.active);
                      return (
                        <Tr key={p.id || idx}>
                          <Td pl="0px" borderColor={borderColor}>
                            <Checkbox
                              isChecked={selectedProductIds.includes(p.id)}
                              onChange={(e) => {
                                setSelectedProductIds(prev => {
                                  if (e.target.checked) return [...new Set([...prev, p.id])];
                                  return prev.filter(id => id !== p.id);
                                });
                              }}
                            />
                          </Td>
                          <Td pl="0px" borderColor={borderColor}>
                            <Avatar src={firstImage} name={p?.name} borderRadius="12px" />
                          </Td>
                          <Td borderColor={borderColor}>
                            <Text fontWeight="bold" color={textColor}>{p?.name}</Text>
                            <Text fontSize="sm" color="gray.400" noOfLines={1}>{p?.description}</Text>
                          </Td>
                          <Td borderColor={borderColor}>
                            <Text color={textColor}>{companyName}</Text>
                          </Td>
                          <Td borderColor={borderColor}>
                            <Badge colorScheme={gender === "female" ? "pink" : gender === "male" ? "blue" : "purple"}>{gender}</Badge>
                          </Td>
                          <Td borderColor={borderColor}>
                            <Text color={textColor} noOfLines={1} maxW="320px">{categories || "-"}</Text>
                          </Td>
                          <Td borderColor={borderColor}>
                            <Badge colorScheme={hasActiveSale ? "green" : "gray"}>{saleText}</Badge>
                          </Td>
                          <Td borderColor={borderColor}>
                            <Text color={textColor}>{createdAt}</Text>
                          </Td>
                         <Td borderColor={borderColor}>
                           <HStack spacing={2}>
                             <Tooltip label="التفاصيل">
                               <IconButton size="sm" icon={<SearchIcon />} onClick={() => openDetails(p)} />
                             </Tooltip>
                             <Tooltip label="تعديل">
                               <IconButton size="sm" colorScheme="blue" variant="outline" icon={<EditIcon />} onClick={() => openEdit(p)} />
                             </Tooltip>
                             <Tooltip label={hasSales ? "عرض التخفيضات" : "لا توجد تخفيضات"}>
                              <IconButton
                                size="sm"
                                colorScheme={hasSales ? (isSaleActive ? "green" : "purple") : "gray"}
                                variant="outline"
                                icon={<Badge colorScheme={hasSales ? (isSaleActive ? "green" : "purple") : "gray"}>{isSaleActive ? "ON" : "%"}</Badge>}
                                onClick={() => openSale(p)}
                                isDisabled={!hasSales}
                              />
                            </Tooltip>
                             <Tooltip label="حذف">
                               <IconButton size="sm" colorScheme="red" variant="outline" icon={<DeleteIcon />} onClick={() => askDelete(p)} />
                             </Tooltip>
                           </HStack>
                         </Td>
                        </Tr>
                      );
                    })}
                  </Tbody>
                </Table>
              </TableContainer>

              {Array.isArray(products) && products.length > 0 && (
                <Flex justify="center" align="center" mt={6} gap={2} flexWrap="wrap" paddingBottom="20px">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handlePageChange(1)}
                    isDisabled={pagination.current_page <= 1 || isLoadingPagination}
                    isLoading={isLoadingPagination && pagination.current_page === 1}
                  >
                    الأولى
                  </Button>

                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handlePageChange(pagination.current_page - 1)}
                    isDisabled={pagination.current_page <= 1 || isLoadingPagination}
                    leftIcon={<ChevronLeftIcon />}
                    isLoading={isLoadingPagination}
                  >
                    السابق
                  </Button>

                  {Array.from({ length: Math.min(5, pagination.last_page) }, (_, i) => {
                    let pageNum;
                    if (pagination.last_page <= 5) {
                      pageNum = i + 1;
                    } else if (pagination.current_page <= 3) {
                      pageNum = i + 1;
                    } else if (pagination.current_page >= pagination.last_page - 2) {
                      pageNum = pagination.last_page - 4 + i;
                    } else {
                      pageNum = pagination.current_page - 2 + i;
                    }

                    if (pageNum < 1 || pageNum > pagination.last_page) return null;

                    return (
                      <Button
                        key={pageNum}
                        size="sm"
                        variant={pagination.current_page === pageNum ? "solid" : "outline"}
                        colorScheme={pagination.current_page === pageNum ? "blue" : "gray"}
                        onClick={() => handlePageChange(pageNum)}
                        isLoading={isLoadingPagination}
                        isDisabled={isLoadingPagination}
                      >
                        {pageNum}
                      </Button>
                    );
                  })}

                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handlePageChange(pagination.current_page + 1)}
                    isDisabled={pagination.current_page >= pagination.last_page || isLoadingPagination}
                    rightIcon={<ChevronRightIcon />}
                    isLoading={isLoadingPagination}
                  >
                    التالي
                  </Button>

                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handlePageChange(pagination.last_page)}
                    isDisabled={pagination.current_page >= pagination.last_page || isLoadingPagination}
                    isLoading={isLoadingPagination && pagination.current_page === pagination.last_page}
                  >
                    الأخيرة
                  </Button>

                  <Text fontSize="sm" color="gray.600" ml={4}>
                    إجمالي {pagination.total} منتج
                  </Text>

                  <HStack spacing={2} ml={4}>
                    <Text fontSize="sm" color="gray.600">اذهب إلى:</Text>
                    <Input
                      size="sm"
                      w="60px"
                      type="number"
                      min={1}
                      max={pagination.last_page}
                      value={pagination.current_page}
                      onChange={(e) => {
                        const page = parseInt(e.target.value);
                        if (page >= 1 && page <= pagination.last_page) {
                          setPagination(prev => ({ ...prev, current_page: page }));
                        }
                      }}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          const page = parseInt(e.target.value);
                          if (page >= 1 && page <= pagination.last_page) {
                            handlePageChange(page);
                          }
                        }
                      }}
                    />
                    <Text fontSize="sm" color="gray.600">من {pagination.last_page}</Text>
                  </HStack>
                </Flex>
              )}
            </>
          )}
         {/* Details Modal */}
         <Modal isOpen={isDetailsOpen} onClose={onDetailsClose} size="4xl">
           <ModalOverlay />
           <ModalContent
             mx={4}
             my={0}
             sx={{ marginTop: '100px !important', marginBottom: '30px !important' }}
             maxH="calc(100vh - 60px)"
             display="flex"
             flexDirection="column"
           >
             <ModalHeader>تفاصيل المنتج</ModalHeader>
             <ModalCloseButton />
             <ModalBody flex="1" overflowY="auto" maxH="calc(100vh - 160px)">
               {isLoadingDetails || !selectedProduct ? (
                 <Flex align="center" justify="center" py={8} gap={3}><Spinner /><Text>جاري التحميل...</Text></Flex>
               ) : (
                 <VStack align="stretch" spacing={5}>
                   <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                     <FormControl>
                       <FormLabel>الاسم</FormLabel>
                       <Input value={selectedProduct.name || ''} isReadOnly />
                     </FormControl>
                     <FormControl>
                       <FormLabel>الشركة</FormLabel>
                       <Input value={selectedProduct.company?.name || selectedProduct.company_id || ''} isReadOnly />
                     </FormControl>
                     <FormControl>
                       <FormLabel>الجنس</FormLabel>
                       <Select value={selectedProduct.gender || ''} isDisabled>
                         <option value="male">male</option>
                         <option value="female">female</option>
                         <option value="unisex">unisex</option>
                       </Select>
                     </FormControl>
                     <FormControl>
                       <FormLabel>النقاط</FormLabel>
                       <Input value={selectedProduct.points ?? 0} isReadOnly />
                     </FormControl>
                     <FormControl>
                       <FormLabel>تخفيض</FormLabel>
                       <HStack>
                         <Switch isChecked={!!selectedProduct.is_on_sale} isDisabled />
                         <Badge colorScheme={selectedProduct.is_on_sale ? 'green' : 'gray'}>
                           {selectedProduct.is_on_sale ? 'مفعل' : 'غير مفعل'}
                         </Badge>
                       </HStack>
                     </FormControl>
                     <FormControl>
                       <FormLabel>التواريخ</FormLabel>
                       <Input value={`أنشئ: ${new Date(selectedProduct.created_at).toLocaleDateString('en-US')} | عدل: ${new Date(selectedProduct.updated_at).toLocaleDateString('en-US')}`} isReadOnly />
                     </FormControl>
                   </SimpleGrid>

                   <FormControl>
                     <FormLabel>الوصف</FormLabel>
                     <Textarea value={selectedProduct.description || ''} isReadOnly />
                   </FormControl>

                   <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                     <FormControl>
                       <FormLabel>الفئات</FormLabel>
                       <Wrap>
                         {(selectedProduct.categories || []).map(c => (
                           <WrapItem key={c.id}><Tag>{c.en_name || c.name}</Tag></WrapItem>
                         ))}
                       </Wrap>
                     </FormControl>
                     <FormControl>
                       <FormLabel>الوسوم</FormLabel>
                       <Wrap>
                         {(selectedProduct.tags || []).map((t, i) => (
                           <WrapItem key={i}><Tag>{t}</Tag></WrapItem>
                         ))}
                       </Wrap>
                     </FormControl>
                   </SimpleGrid>

                   <Divider />

                   <FormControl>
                     <FormLabel>الصور</FormLabel>
                     <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3}>
                       {(selectedProduct.images || []).map((img, i) => (
                         <Box key={i} position="relative" borderRadius="md" overflow="hidden" border="1px solid" borderColor={borderColor}>
                           <Image src={img.url} alt={`image-${i}`} objectFit="cover" w="100%" h="120px" />
                           <Badge position="absolute" top={2} left={2} colorScheme={img.type === 'video' ? 'purple' : 'teal'}>{img.type}</Badge>
                         </Box>
                       ))}
                     </SimpleGrid>
                   </FormControl>

                   <Divider />

                   <FormControl>
                     <FormLabel>التوفر في الفروع</FormLabel>
                     {(selectedProduct.branch_products && selectedProduct.branch_products.length > 0) ? (
                       <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                         {selectedProduct.branch_products.map((bp, idx) => (
                           <Box key={bp.id || idx} border="1px solid" borderColor={borderColor} borderRadius="md" p={3}>
                             <HStack justify="space-between" mb={2}>
                               <Text fontWeight="bold">{bp.branch?.title || `فرع #${bp.branch_id}`}</Text>
                               <Badge colorScheme="blue">السعر: {bp.price}</Badge>
                             </HStack>
                             {(bp.product_storage && bp.product_storage.length > 0) ? (
                               <Table size="sm" variant="simple">
                                 <Thead>
                                   <Tr>
                                     <Th>اللون</Th>
                                     <Th>المقاس</Th>
                                     <Th isNumeric>الكمية</Th>
                                   </Tr>
                                 </Thead>
                                 <Tbody>
                                   {bp.product_storage.map((ps) => (
                                     <Tr key={ps.id}>
                                       <Td>
                                         <HStack>
                                           <Box w="12px" h="12px" borderRadius="full" bg={ps.color?.hash_code || 'gray.300'} border="1px solid" borderColor="gray.200" />
                                           <Text>{ps.color?.name || ps.color_id}</Text>
                                         </HStack>
                                       </Td>
                                       <Td>
                                         <Text>{ps.size?.name || ps.size_id}</Text>
                                       </Td>
                                       <Td isNumeric>
                                         <Text>{ps.quantity}</Text>
                                       </Td>
                                     </Tr>
                                   ))}
                                 </Tbody>
                               </Table>
                             ) : (
                               <Text color="gray.500">لا يوجد مخزون لهذا الفرع</Text>
                             )}
                           </Box>
                         ))}
                       </SimpleGrid>
                     ) : (
                       <Text color="gray.500">لا توجد فروع مرتبطة بهذا المنتج</Text>
                     )}
                   </FormControl>
                 </VStack>
               )}
             </ModalBody>
             <ModalFooter>
               <Button onClick={onDetailsClose}>إغلاق</Button>
             </ModalFooter>
           </ModalContent>
         </Modal>

         {/* Edit Modal */}
         <Modal isOpen={isEditOpen} onClose={onEditClose} size="4xl">
           <ModalOverlay />
           <ModalContent
             mx={4}
             my={0}
             sx={{ marginTop: '100px !important', marginBottom: '30px !important' }}
             maxH="calc(100vh - 60px)"
             display="flex"
             flexDirection="column"
           >
             <ModalHeader>تعديل المنتج</ModalHeader>
             <ModalCloseButton />
             <ModalBody flex="1" overflowY="auto" maxH="calc(100vh - 160px)">
               <VStack align="stretch" spacing={5}>
                 <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                   <FormControl>
                     <FormLabel>الشركة</FormLabel>
                     <Select value={editCompanyId} onChange={(e) => setEditCompanyId(e.target.value)}>
                       {(filtersMeta.companies || []).map(c => (
                         <option key={c.id} value={c.id}>{c.id} - {c.name}</option>
                       ))}
                     </Select>
                   </FormControl>
                   <FormControl>
                     <FormLabel>الاسم</FormLabel>
                     <Input value={editForm.name} onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))} />
                   </FormControl>
                   <FormControl>
                     <FormLabel>الجنس</FormLabel>
                     <Select value={editForm.gender} onChange={(e) => setEditForm(prev => ({ ...prev, gender: e.target.value }))}>
                       <option value="male">male</option>
                       <option value="female">female</option>
                       <option value="unisex">unisex</option>
                     </Select>
                   </FormControl>
                   <FormControl>
                     <FormLabel>النقاط</FormLabel>
                     <Input type="number" value={editForm.points} onChange={(e) => setEditForm(prev => ({ ...prev, points: Number(e.target.value || 0) }))} />
                   </FormControl>
                 </SimpleGrid>

                 <FormControl>
                   <FormLabel>الوصف</FormLabel>
                   <Textarea value={editForm.description} onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))} />
                 </FormControl>

                 <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                   <FormControl display="flex" alignItems="center" gap={3}>
                     <FormLabel m={0}>تخفيض</FormLabel>
                     <Switch isChecked={!!editForm.is_on_sale} onChange={(e) => setEditForm(prev => ({ ...prev, is_on_sale: e.target.checked }))} />
                   </FormControl>
                   <FormControl display="flex" alignItems="center" gap={3}>
                     <FormLabel m={0}>قابل للاستبدال</FormLabel>
                     <Switch isChecked={!!editFlags.is_replaceable} onChange={(e) => setEditFlags(prev => ({ ...prev, is_replaceable: e.target.checked }))} />
                   </FormControl>
                   <FormControl display="flex" alignItems="center" gap={3}>
                     <FormLabel m={0}>قابل للاسترجاع</FormLabel>
                     <Switch isChecked={!!editFlags.is_refundable} onChange={(e) => setEditFlags(prev => ({ ...prev, is_refundable: e.target.checked }))} />
                   </FormControl>
                 </SimpleGrid>

                 <FormControl>
                   <FormLabel>الفئات</FormLabel>
                   <SimpleGrid columns={{ base: 2, md: 3, lg: 4 }} spacing={2}>
                     {(filtersMeta.sub_categories || []).map(s => {
                       const selected = editCategories.includes(s.id);
                       return (
                         <Button
                           key={s.id}
                           size="sm"
                           variant={selected ? 'solid' : 'outline'}
                           colorScheme={selected ? 'blue' : 'gray'}
                           onClick={() => {
                             setEditCategories(prev => prev.includes(s.id) ? prev.filter(id => id !== s.id) : [...prev, s.id]);
                           }}
                           justifyContent="flex-start"
                         >
                           {s.id} - {(s.en_name || s.name)}
                         </Button>
                       );
                     })}
                   </SimpleGrid>
                 </FormControl>

                 <FormControl>
                   <FormLabel>الفروع والأسعار والمخزون</FormLabel>
                   <VStack align="stretch" spacing={3}>
                     {editBranches.map((b, bi) => (
                       <Box key={bi} border="1px solid" borderColor={borderColor} p={3} borderRadius="md">
                         <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3} mb={3}>
                           <Select value={b.branch_id} onChange={(e) => {
                             const v = Number(e.target.value);
                             setEditBranches(prev => prev.map((x,i)=> i===bi?{...x, branch_id: v}:x));
                           }}>
                             {(companyBranches || []).map(br => (
                               <option key={br.id} value={br.id}>{br.id} - {br.title}</option>
                             ))}
                           </Select>
                           <Input type="number" placeholder="السعر" value={b.price}
                             min={1}
                             step={1}
                             onChange={(e)=>{
                               const v = Math.max(1, parseFloat(e.target.value || '1'));
                               setEditBranches(prev => prev.map((x,i)=> i===bi?{...x, price: v}:x));
                             }} />
                           <Button size="sm" colorScheme="red" variant="outline" onClick={()=> setEditBranches(prev => prev.filter((_,i)=> i!==bi))}>حذف الفرع</Button>
                         </SimpleGrid>
                         <VStack align="stretch" spacing={2}>
                           {b.colors.map((c, ci) => (
                             <Box key={ci} border="1px dashed" borderColor={borderColor} p={2} borderRadius="md">
                               <HStack mb={2}>
                                 <Select value={c.color_id} onChange={(e)=>{
                                   const v = Number(e.target.value);
                                   setEditBranches(prev => prev.map((br,i)=> i===bi?{...br, colors: br.colors.map((cc,j)=> j===ci?{...cc, color_id: v}:cc)}:br));
                                 }} w="220px">
                                   {(filtersMeta.colors || []).map(col => (
                                     <option key={col.id} value={col.id}>{col.name}</option>
                                   ))}
                                 </Select>
                                 <Button size="xs" variant="outline" colorScheme="red" onClick={()=>{
                                   setEditBranches(prev => prev.map((br,i)=> i===bi?{...br, colors: br.colors.filter((_,j)=> j!==ci)}:br));
                                 }}>حذف اللون</Button>
                               </HStack>
                               <VStack align="stretch" spacing={2}>
                                 {c.sizes.map((s, si) => (
                                   <HStack key={si}>
                                     <Select value={s.size_id} onChange={(e)=>{
                                       const v = Number(e.target.value);
                                       setEditBranches(prev => prev.map((br,i)=> i===bi?{...br, colors: br.colors.map((cc,j)=> j===ci?{...cc, sizes: cc.sizes.map((ss,k)=> k===si?{...ss, size_id: v}:ss)}:cc)}:br));
                                     }} w="200px">
                                       {(filtersMeta.sizes || []).map(sz => (
                                         <option key={sz.id} value={sz.id}>{sz.name || sz.id}</option>
                                       ))}
                                     </Select>
                                     <Input type="number" placeholder="الكمية" value={s.quantity}
                                       min={1}
                                       step={1}
                                       onChange={(e)=>{
                                         const v = Math.max(1, parseInt(e.target.value || '1', 10));
                                         setEditBranches(prev => prev.map((br,i)=> i===bi?{...br, colors: br.colors.map((cc,j)=> j===ci?{...cc, sizes: cc.sizes.map((ss,k)=> k===si?{...ss, quantity: v}:ss)}:cc)}:br));
                                       }} w="140px" />
                                     <Button size="xs" variant="outline" colorScheme="red" onClick={()=>{
                                       setEditBranches(prev => prev.map((br,i)=> i===bi?{...br, colors: br.colors.map((cc,j)=> j===ci?{...cc, sizes: cc.sizes.filter((_,k)=> k!==si)}:cc)}:br));
                                     }}>حذف المقاس</Button>
                                   </HStack>
                                 ))}
                                 <Button size="xs" onClick={()=>{
                                   setEditBranches(prev => prev.map((br,i)=> i===bi?{...br, colors: br.colors.map((cc,j)=> j===ci?{...cc, sizes: [...cc.sizes, { size_id: (filtersMeta.sizes?.[0]?.id)||'', quantity: 1 }]}:cc)}:br));
                                 }}>إضافة مقاس</Button>
                               </VStack>
                             </Box>
                           ))}
                           <Button size="sm" onClick={()=>{
                             setEditBranches(prev => prev.map((br,i)=> i===bi?{...br, colors: [...br.colors, { color_id: (filtersMeta.colors?.[0]?.id)||'', sizes: [] }]}:br));
                           }}>إضافة لون</Button>
                         </VStack>
                       </Box>
                     ))}
                     <Button onClick={()=> setEditBranches(prev => [...prev, { branch_id: companyBranches?.[0]?.id || '', price: 1, colors: [] }])}>إضافة فرع</Button>
                   </VStack>
                 </FormControl>

                 <FormControl>
                   <FormLabel>صور المنتج (اسحب لتحريك الترتيب، الجديدة تُضاف في النهاية)</FormLabel>
                   <VStack align="stretch" spacing={3}>
                     <HStack overflowX="auto" spacing={3}>
                       {imageItems.map((item, idx) => (
                         <Box
                           key={(item.kind === 'existing' ? item.url : `new-${idx}`)}
                           position="relative"
                           border="1px solid"
                           borderColor={borderColor}
                           borderRadius="md"
                           p={2}
                           minW="140px"
                           draggable
                           onDragStart={() => { dragIndexRef.current = idx; }}
                           onDragOver={(e) => { e.preventDefault(); }}
                           onDrop={() => {
                             const from = dragIndexRef.current;
                             if (from === null || from === idx) return;
                             setImageItems(prev => {
                               const list = [...prev];
                               const [moved] = list.splice(from, 1);
                               list.splice(idx, 0, moved);
                               return list;
                             });
                           }}
                         >
                           <Image src={item.kind === 'existing' ? item.url : URL.createObjectURL(item.file)} alt={`img-${idx}`} objectFit="cover" w="120px" h="120px" borderRadius="sm" />
                           <HStack mt={2} justify="space-between">
                             <Button size="xs" onClick={() => {
                               if (idx === 0) return;
                               setImageItems(prev => {
                                 const arr = [...prev];
                                 [arr[idx-1], arr[idx]] = [arr[idx], arr[idx-1]];
                                 return arr;
                               });
                             }}>يسار</Button>
                             <Button size="xs" onClick={() => {
                               if (idx === imageItems.length - 1) return;
                               setImageItems(prev => {
                                 const arr = [...prev];
                                 [arr[idx], arr[idx+1]] = [arr[idx+1], arr[idx]];
                                 return arr;
                               });
                             }}>يمين</Button>
                             <Button size="xs" colorScheme="red" variant="outline" onClick={() => {
                               setImageItems(prev => prev.filter((_,i)=> i!==idx));
                             }}>حذف</Button>
                           </HStack>
                         </Box>
                       ))}
                     </HStack>
                     <Box
                       p={6}
                       w="100%"
                       border="2px dashed"
                       borderColor={isDragging ? 'blue.400' : borderColor}
                       borderRadius="md"
                       textAlign="center"
                       bg={isDragging ? 'blue.50' : 'transparent'}
                       onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                       onDragLeave={() => setIsDragging(false)}
                       onDrop={(e) => {
                         e.preventDefault();
                         setIsDragging(false);
                         const files = Array.from(e.dataTransfer.files || []).filter(f => (f.type || '').startsWith('image/'));
                         if (files.length === 0) return;
                         setImageItems(prev => ([...prev, ...files.map(f => ({ kind: 'new', file: f }))]));
                       }}
                     >
                       <VStack spacing={2}>
                         <Text color="gray.600">اسحب الصور هنا أو</Text>
                         <Button size="sm" onClick={() => fileInputRef.current?.click()}>اختر ملفات</Button>
                         <Text fontSize="xs" color="gray.500">يمكنك رفع عدة صور (JPEG, PNG, WEBP)</Text>
                       </VStack>
                       <Input
                         ref={fileInputRef}
                         type="file"
                         multiple
                         accept="image/*"
                         display="none"
                         onChange={(e)=> {
                           const files = Array.from(e.target.files || []);
                           if (files.length === 0) return;
                           setImageItems(prev => ([...prev, ...files.map(f => ({ kind: 'new', file: f }))]));
                           if (fileInputRef.current) fileInputRef.current.value = '';
                         }}
                       />
                     </Box>
                   </VStack>
                 </FormControl>
               </VStack>
             </ModalBody>
             <ModalFooter>
               <HStack>
                 <Button variant="ghost" onClick={onEditClose}>إلغاء</Button>
                 <Button colorScheme="blue" onClick={saveEdit} isLoading={actionLoading.update}>حفظ</Button>
               </HStack>
             </ModalFooter>
           </ModalContent>
         </Modal>

         {/* Sales Modal */}
         <Modal isOpen={isSaleOpen} onClose={onSaleClose} isCentered>
           <ModalOverlay />
           <ModalContent
             mx={4}
             my={0}
             sx={{ marginTop: '100px !important', marginBottom: '30px !important' }}
             maxH="calc(100vh - 60px)"
             maxW="none"
             display="flex"
             flexDirection="column"
           >
             <ModalHeader>التخفيضات</ModalHeader>
             <ModalCloseButton />
             <ModalBody flex="1" overflowY="auto" overflowX="auto" maxH="calc(100vh - 160px)">
               {isLoadingDetails || !selectedProduct ? (
                 <Flex align="center" justify="center" py={4} gap={3}><Spinner /><Text>جاري التحميل...</Text></Flex>
               ) : Array.isArray(selectedProduct.sales) && selectedProduct.sales.length > 0 ? (
                 <TableContainer overflowX="auto">
                   <Table variant="simple" size="sm" minW="720px">
                     <Thead>
                       <Tr>
                         <Th>النوع</Th>
                         <Th>القيمة</Th>
                         <Th>من</Th>
                         <Th>إلى</Th>
                         <Th>الحالة</Th>
                         <Th w="120px">إجراءات</Th>
                       </Tr>
                     </Thead>
                     <Tbody>
                       {selectedProduct.sales.map((s) => (
                         <Tr key={s.id}>
                           <Td whiteSpace="nowrap">{s.discount_type}</Td>
                           <Td whiteSpace="nowrap">{s.discount_amount}</Td>
                           <Td whiteSpace="nowrap">{s.start_date ? new Date(s.start_date).toLocaleDateString('en-US') : '-'}</Td>
                           <Td whiteSpace="nowrap">{s.end_date ? new Date(s.end_date).toLocaleDateString('en-US') : '-'}</Td>
                           <Td whiteSpace="nowrap">
                             <Badge colorScheme={s.active ? 'green' : 'gray'}>{s.active ? 'مفعل' : 'غير مفعل'}</Badge>
                           </Td>
                           <Td>
                             <HStack spacing={2}>
                               <Tooltip label="تعديل">
                                 <IconButton size="sm" colorScheme="blue" variant="outline" icon={<EditIcon />} onClick={() => openSaleEdit(s)} />
                               </Tooltip>
                               <Tooltip label="حذف">
                                 <IconButton size="sm" colorScheme="red" variant="outline" icon={<DeleteIcon />} onClick={() => askDeleteSale(s)} />
                               </Tooltip>
                             </HStack>
                           </Td>
                         </Tr>
                       ))}
                     </Tbody>
                   </Table>
                 </TableContainer>
               ) : (
                 <Text>لا توجد تخفيضات لهذا المنتج</Text>
               )}
             </ModalBody>
             <ModalFooter>
               <Button onClick={onSaleClose}>إغلاق</Button>
             </ModalFooter>
           </ModalContent>
         </Modal>

         {/* Edit Sale Modal */}
         <Modal isOpen={isSaleEditOpen} onClose={onSaleEditClose} isCentered>
           <ModalOverlay />
           <ModalContent>
             <ModalHeader>تعديل التخفيض</ModalHeader>
             <ModalCloseButton />
             <ModalBody>
               <VStack align="stretch" spacing={4}>
                 <FormControl>
                   <FormLabel>نوع التخفيض</FormLabel>
                   <Select value={saleEditForm.discount_type} onChange={(e) => setSaleEditForm(prev => ({ ...prev, discount_type: e.target.value }))}>
                     <option value="percentage">percentage</option>
                     <option value="amount">amount</option>
                   </Select>
                 </FormControl>
                 <FormControl>
                   <FormLabel>قيمة التخفيض</FormLabel>
                   <Input type="number" step="0.1" min={0} value={saleEditForm.discount_amount} onChange={(e)=> {
                     const v = Math.max(0, parseFloat(e.target.value || '0'));
                     setSaleEditForm(prev => ({ ...prev, discount_amount: v }));
                   }} />
                 </FormControl>
                 <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                   <FormControl>
                     <FormLabel>تاريخ البداية</FormLabel>
                     <Input type="date" value={saleEditForm.start_date} onChange={(e)=> setSaleEditForm(prev => ({ ...prev, start_date: e.target.value }))} />
                   </FormControl>
                   <FormControl>
                     <FormLabel>تاريخ النهاية</FormLabel>
                     <Input type="date" value={saleEditForm.end_date} onChange={(e)=> setSaleEditForm(prev => ({ ...prev, end_date: e.target.value }))} />
                   </FormControl>
                 </SimpleGrid>
                 {areDatesInvalid(saleEditForm.start_date, saleEditForm.end_date) && (
                   <Text color="red.500" fontSize="sm">تاريخ البداية يجب أن يكون قبل تاريخ النهاية</Text>
                 )}
               </VStack>
             </ModalBody>
             <ModalFooter>
               <HStack>
                 <Button variant="ghost" onClick={onSaleEditClose}>إلغاء</Button>
                 <Button colorScheme="blue" onClick={saveSaleEdit} isLoading={actionLoading.saleUpdate} isDisabled={areDatesInvalid(saleEditForm.start_date, saleEditForm.end_date)}>حفظ</Button>
               </HStack>
             </ModalFooter>
           </ModalContent>
         </Modal>

         {/* Bulk Sale Modal */}
         <Modal isOpen={isBulkSaleOpen} onClose={onBulkSaleClose} isCentered>
           <ModalOverlay />
           <ModalContent>
             <ModalHeader>إضافة تخفيض (متعدد)</ModalHeader>
             <ModalCloseButton />
             <ModalBody>
               <VStack align="stretch" spacing={4}>
                 <FormControl>
                   <FormLabel>نوع التخفيض</FormLabel>
                   <Select value={bulkSaleForm.discount_type} onChange={(e) => setBulkSaleForm(prev => ({ ...prev, discount_type: e.target.value }))}>
                     <option value="percentage">percentage</option>
                     <option value="amount">amount</option>
                   </Select>
                 </FormControl>
                 <FormControl>
                   <FormLabel>قيمة التخفيض</FormLabel>
                   <Input type="number" step="0.1" min={0} value={bulkSaleForm.discount_amount} onChange={(e)=> {
                     const v = Math.max(0, parseFloat(e.target.value || '0'));
                     setBulkSaleForm(prev => ({ ...prev, discount_amount: v }));
                   }} />
                 </FormControl>
                 <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                   <FormControl>
                     <FormLabel>تاريخ البداية</FormLabel>
                     <Input type="date" value={bulkSaleForm.start_date} onChange={(e)=> setBulkSaleForm(prev => ({ ...prev, start_date: e.target.value }))} />
                   </FormControl>
                   <FormControl>
                     <FormLabel>تاريخ النهاية</FormLabel>
                     <Input type="date" value={bulkSaleForm.end_date} onChange={(e)=> setBulkSaleForm(prev => ({ ...prev, end_date: e.target.value }))} />
                   </FormControl>
                 </SimpleGrid>
                 {areDatesInvalid(bulkSaleForm.start_date, bulkSaleForm.end_date) && (
                   <Text color="red.500" fontSize="sm">تاريخ البداية يجب أن يكون قبل تاريخ النهاية</Text>
                 )}
               </VStack>
             </ModalBody>
             <ModalFooter>
               <HStack>
                 <Button variant="ghost" onClick={onBulkSaleClose}>إلغاء</Button>
                 <Button colorScheme="purple" onClick={submitBulkSale} isLoading={actionLoading.saleUpdate} isDisabled={areDatesInvalid(bulkSaleForm.start_date, bulkSaleForm.end_date)}>حفظ</Button>
               </HStack>
             </ModalFooter>
           </ModalContent>
         </Modal>

         {/* Delete Sale Confirm */}
         <AlertDialog isOpen={isSaleDeleteOpen} onClose={onSaleDeleteClose} isCentered>
           <AlertDialogOverlay />
           <AlertDialogContent mx={4}>
             <AlertDialogHeader fontSize="xl" fontWeight="bold">حذف التخفيض</AlertDialogHeader>
             <AlertDialogBody fontSize="md">
               هل أنت متأكد من حذف التخفيض المحدد؟ لا يمكن التراجع عن هذا الإجراء.
             </AlertDialogBody>
             <AlertDialogFooter justifyContent="flex-end">
               <HStack spacing={3}>
                 <Button variant="outline" onClick={onSaleDeleteClose} minW="100px">إلغاء</Button>
                 <Button colorScheme="red" onClick={confirmDeleteSale} isLoading={actionLoading.saleDelete} minW="100px">حذف</Button>
               </HStack>
             </AlertDialogFooter>
           </AlertDialogContent>
         </AlertDialog>

         {/* Create Modal */}
         <Modal isOpen={isCreateOpen} onClose={() => setIsCreateOpen(false)} size="4xl">
           <ModalOverlay />
           <ModalContent
             mx={4}
             my={0}
             sx={{ marginTop: '100px !important', marginBottom: '30px !important' }}
             maxH="calc(100vh - 60px)"
             display="flex"
             flexDirection="column"
           >
             <ModalHeader>إضافة منتج</ModalHeader>
             <ModalCloseButton />
             <ModalBody flex="1" overflowY="auto" maxH="calc(100vh - 160px)">
               <VStack align="stretch" spacing={5}>
                 <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                   <FormControl>
                     <FormLabel>الشركة</FormLabel>
                     <Select value={editCompanyId} onChange={(e) => setEditCompanyId(e.target.value)}>
                       {(filtersMeta.companies || []).map(c => (
                         <option key={c.id} value={c.id}>{c.id} - {c.name}</option>
                       ))}
                     </Select>
                   </FormControl>
                   <FormControl>
                     <FormLabel>الاسم</FormLabel>
                     <Input value={editForm.name} onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))} />
                   </FormControl>
                   <FormControl>
                     <FormLabel>الجنس</FormLabel>
                     <Select value={editForm.gender} onChange={(e) => setEditForm(prev => ({ ...prev, gender: e.target.value }))}>
                       <option value="male">male</option>
                       <option value="female">female</option>
                       <option value="unisex">unisex</option>
                     </Select>
                   </FormControl>
                   <FormControl>
                     <FormLabel>النقاط</FormLabel>
                     <Input type="number" value={editForm.points} onChange={(e) => setEditForm(prev => ({ ...prev, points: Number(e.target.value || 0) }))} />
                   </FormControl>
                 </SimpleGrid>

                 <FormControl>
                   <FormLabel>الوصف</FormLabel>
                   <Textarea value={editForm.description} onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))} />
                 </FormControl>

                 <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                   <FormControl display="flex" alignItems="center" gap={3}>
                     <FormLabel m={0}>تخفيض</FormLabel>
                     <Switch isChecked={!!editForm.is_on_sale} onChange={(e) => setEditForm(prev => ({ ...prev, is_on_sale: e.target.checked }))} />
                   </FormControl>
                   <FormControl display="flex" alignItems="center" gap={3}>
                     <FormLabel m={0}>قابل للاستبدال</FormLabel>
                     <Switch isChecked={!!editFlags.is_replaceable} onChange={(e) => setEditFlags(prev => ({ ...prev, is_replaceable: e.target.checked }))} />
                   </FormControl>
                   <FormControl display="flex" alignItems="center" gap={3}>
                     <FormLabel m={0}>قابل للاسترجاع</FormLabel>
                     <Switch isChecked={!!editFlags.is_refundable} onChange={(e) => setEditFlags(prev => ({ ...prev, is_refundable: e.target.checked }))} />
                   </FormControl>
                 </SimpleGrid>

                 <FormControl>
                   <FormLabel>الفئات</FormLabel>
                   <SimpleGrid columns={{ base: 2, md: 3, lg: 4 }} spacing={2}>
                     {(filtersMeta.sub_categories || []).map(s => {
                       const selected = editCategories.includes(s.id);
                       return (
                         <Button
                           key={s.id}
                           size="sm"
                           variant={selected ? 'solid' : 'outline'}
                           colorScheme={selected ? 'blue' : 'gray'}
                           onClick={() => {
                             setEditCategories(prev => prev.includes(s.id) ? prev.filter(id => id !== s.id) : [...prev, s.id]);
                           }}
                           justifyContent="flex-start"
                         >
                           {s.id} - {(s.en_name || s.name)}
                         </Button>
                       );
                     })}
                   </SimpleGrid>
                 </FormControl>

                 <FormControl>
                   <FormLabel>الفروع والأسعار والمخزون</FormLabel>
                   <VStack align="stretch" spacing={3}>
                     {editBranches.map((b, bi) => (
                       <Box key={bi} border="1px solid" borderColor={borderColor} p={3} borderRadius="md">
                         <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3} mb={3}>
                           <Select value={b.branch_id} onChange={(e) => {
                             const v = Number(e.target.value);
                             setEditBranches(prev => prev.map((x,i)=> i===bi?{...x, branch_id: v}:x));
                           }}>
                             {(companyBranches || []).map(br => (
                               <option key={br.id} value={br.id}>{br.id} - {br.title}</option>
                             ))}
                           </Select>
                           <Input type="number" placeholder="السعر" value={b.price}
                             min={0.01}
                             step={0.01}
                             onChange={(e)=>{
                               const v = Math.max(0.01, parseFloat(e.target.value || '0.01'));
                               setEditBranches(prev => prev.map((x,i)=> i===bi?{...x, price: v}:x));
                             }} />
                           <Button size="sm" colorScheme="red" variant="outline" onClick={()=> setEditBranches(prev => prev.filter((_,i)=> i!==bi))}>حذف الفرع</Button>
                         </SimpleGrid>
                         <VStack align="stretch" spacing={2}>
                           {b.colors.map((c, ci) => (
                             <Box key={ci} border="1px dashed" borderColor={borderColor} p={2} borderRadius="md">
                               <HStack mb={2}>
                                 <Select value={c.color_id} onChange={(e)=>{
                                   const v = Number(e.target.value);
                                   setEditBranches(prev => prev.map((br,i)=> i===bi?{...br, colors: br.colors.map((cc,j)=> j===ci?{...cc, color_id: v}:cc)}:br));
                                 }} w="220px">
                                   {(filtersMeta.colors || []).map(col => (
                                     <option key={col.id} value={col.id}>{col.name}</option>
                                   ))}
                                 </Select>
                                 <Button size="xs" variant="outline" colorScheme="red" onClick={()=>{
                                   setEditBranches(prev => prev.map((br,i)=> i===bi?{...br, colors: br.colors.filter((_,j)=> j!==ci)}:br));
                                 }}>حذف اللون</Button>
                               </HStack>
                               <VStack align="stretch" spacing={2}>
                                 {c.sizes.map((s, si) => (
                                   <HStack key={si}>
                                     <Select value={s.size_id} onChange={(e)=>{
                                       const v = Number(e.target.value);
                                       setEditBranches(prev => prev.map((br,i)=> i===bi?{...br, colors: br.colors.map((cc,j)=> j===ci?{...cc, sizes: cc.sizes.map((ss,k)=> k===si?{...ss, size_id: v}:ss)}:cc)}:br));
                                     }} w="200px">
                                       {(filtersMeta.sizes || []).map(sz => (
                                         <option key={sz.id} value={sz.id}>{sz.name || sz.id}</option>
                                       ))}
                                     </Select>
                                     <Input type="number" placeholder="الكمية" value={s.quantity}
                                       min={1}
                                       step={1}
                                       onChange={(e)=>{
                                         const v = Math.max(1, parseInt(e.target.value || '1', 10));
                                         setEditBranches(prev => prev.map((br,i)=> i===bi?{...br, colors: br.colors.map((cc,j)=> j===ci?{...cc, sizes: cc.sizes.map((ss,k)=> k===si?{...ss, quantity: v}:ss)}:cc)}:br));
                                       }} w="140px" />
                                     <Button size="xs" variant="outline" colorScheme="red" onClick={()=>{
                                       setEditBranches(prev => prev.map((br,i)=> i===bi?{...br, colors: br.colors.map((cc,j)=> j===ci?{...cc, sizes: cc.sizes.filter((_,k)=> k!==si)}:cc)}:br));
                                     }}>حذف المقاس</Button>
                                   </HStack>
                                 ))}
                                 <Button size="xs" onClick={()=>{
                                   setEditBranches(prev => prev.map((br,i)=> i===bi?{...br, colors: br.colors.map((cc,j)=> j===ci?{...cc, sizes: [...cc.sizes, { size_id: (filtersMeta.sizes?.[0]?.id)||'', quantity: 1 }]}:cc)}:br));
                                 }}>إضافة مقاس</Button>
                               </VStack>
                             </Box>
                           ))}
                           <Button size="sm" onClick={()=>{
                             setEditBranches(prev => prev.map((br,i)=> i===bi?{...br, colors: [...br.colors, { color_id: (filtersMeta.colors?.[0]?.id)||'', sizes: [] }]}:br));
                           }}>إضافة لون</Button>
                         </VStack>
                       </Box>
                     ))}
                     <Button onClick={()=> setEditBranches(prev => [...prev, { branch_id: companyBranches?.[0]?.id || '', price: 1, colors: [] }])}>إضافة فرع</Button>
                   </VStack>
                 </FormControl>

                 <FormControl>
                   <FormLabel>صور المنتج (اسحب لتحريك الترتيب، الجديدة تُضاف في النهاية)</FormLabel>
                   <VStack align="stretch" spacing={3}>
                     <HStack overflowX="auto" spacing={3}>
                       {imageItems.map((item, idx) => (
                         <Box
                           key={(item.kind === 'existing' ? item.url : `new-${idx}`)}
                           position="relative"
                           border="1px solid"
                           borderColor={borderColor}
                           borderRadius="md"
                           p={2}
                           minW="140px"
                           draggable
                           onDragStart={() => { dragIndexRef.current = idx; }}
                           onDragOver={(e) => { e.preventDefault(); }}
                           onDrop={() => {
                             const from = dragIndexRef.current;
                             if (from === null || from === idx) return;
                             setImageItems(prev => {
                               const list = [...prev];
                               const [moved] = list.splice(from, 1);
                               list.splice(idx, 0, moved);
                               return list;
                             });
                           }}
                         >
                           <Image src={item.kind === 'existing' ? item.url : URL.createObjectURL(item.file)} alt={`img-${idx}`} objectFit="cover" w="120px" h="120px" borderRadius="sm" />
                           <HStack mt={2} justify="space-between">
                             <Button size="xs" onClick={() => {
                               if (idx === 0) return;
                               setImageItems(prev => {
                                 const arr = [...prev];
                                 [arr[idx-1], arr[idx]] = [arr[idx], arr[idx-1]];
                                 return arr;
                               });
                             }}>يسار</Button>
                             <Button size="xs" onClick={() => {
                               if (idx === imageItems.length - 1) return;
                               setImageItems(prev => {
                                 const arr = [...prev];
                                 [arr[idx], arr[idx+1]] = [arr[idx+1], arr[idx]];
                                 return arr;
                               });
                             }}>يمين</Button>
                             <Button size="xs" colorScheme="red" variant="outline" onClick={() => {
                               setImageItems(prev => prev.filter((_,i)=> i!==idx));
                             }}>حذف</Button>
                           </HStack>
                         </Box>
                       ))}
                     </HStack>
                     <Box
                       p={6}
                       w="100%"
                       border="2px dashed"
                       borderColor={isDragging ? 'blue.400' : borderColor}
                       borderRadius="md"
                       textAlign="center"
                       bg={isDragging ? 'blue.50' : 'transparent'}
                       onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                       onDragLeave={() => setIsDragging(false)}
                       onDrop={(e) => {
                         e.preventDefault();
                         setIsDragging(false);
                         const files = Array.from(e.dataTransfer.files || []).filter(f => (f.type || '').startsWith('image/'));
                         if (files.length === 0) return;
                         setImageItems(prev => ([...prev, ...files.map(f => ({ kind: 'new', file: f }))]));
                       }}
                     >
                       <VStack spacing={2}>
                         <Text color="gray.600">اسحب الصور هنا أو</Text>
                         <Button size="sm" onClick={() => fileInputRef.current?.click()}>اختر ملفات</Button>
                         <Text fontSize="xs" color="gray.500">يمكنك رفع عدة صور (JPEG, PNG, WEBP)</Text>
                       </VStack>
                       <Input
                         ref={fileInputRef}
                         type="file"
                         multiple
                         accept="image/*"
                         display="none"
                         onChange={(e)=> {
                           const files = Array.from(e.target.files || []);
                           if (files.length === 0) return;
                           setImageItems(prev => ([...prev, ...files.map(f => ({ kind: 'new', file: f }))]));
                           if (fileInputRef.current) fileInputRef.current.value = '';
                         }}
                       />
                     </Box>
                   </VStack>
                 </FormControl>
               </VStack>
             </ModalBody>
             <ModalFooter>
               <HStack>
                 <Button variant="ghost" onClick={() => setIsCreateOpen(false)}>إلغاء</Button>
                 <Button colorScheme="blue" isLoading={actionLoading.update} onClick={async ()=>{
                   setActionLoading(prev => ({ ...prev, update: true }));
                   try {
                     const newFiles = imageItems.filter(it => it.kind === 'new').map(it => it.file);
                     const payload = {
                       name: editForm.name,
                       description: editForm.description,
                       gender: editForm.gender,
                       is_on_sale: !!editForm.is_on_sale,
                       is_replaceable: !!editFlags.is_replaceable,
                       is_refundable: !!editFlags.is_refundable,
                       points: editForm.points || 0,
                       company_id: editCompanyId,
                       categories: editCategories,
                       branches: editBranches,
                       images: newFiles,
                     };
                     await productsService.createProduct(payload);
                     toast({ title: 'تم الإنشاء', description: 'تم إضافة المنتج', status: 'success', duration: 3000, isClosable: true });
                     setIsCreateOpen(false);
                     fetchPage(1);
                   } catch (e) {
                     toast({ title: 'خطأ', description: 'تعذر إنشاء المنتج', status: 'error', duration: 4000, isClosable: true });
                   } finally {
                     setActionLoading(prev => ({ ...prev, update: false }));
                   }
                 }}>حفظ</Button>
               </HStack>
             </ModalFooter>
           </ModalContent>
         </Modal>

         {/* Delete Confirm */}
         <AlertDialog isOpen={isDeleteOpen} onClose={onDeleteClose} isCentered>
           <AlertDialogOverlay />
           <AlertDialogContent mx={4}>
             <AlertDialogHeader fontSize="xl" fontWeight="bold">حذف المنتج</AlertDialogHeader>
             <AlertDialogBody fontSize="md">
               هل أنت متأكد من حذف المنتج "{deletingProduct?.name}"؟ لا يمكن التراجع عن هذا الإجراء.
             </AlertDialogBody>
             <AlertDialogFooter justifyContent="flex-end">
               <HStack spacing={3}>
                 <Button variant="outline" onClick={onDeleteClose} minW="100px">إلغاء</Button>
                 <Button colorScheme="red" onClick={confirmDelete} isLoading={actionLoading.delete} minW="100px">حذف</Button>
               </HStack>
             </AlertDialogFooter>
           </AlertDialogContent>
         </AlertDialog>
        </CardBody>
      </Card>
    </Flex>
  );
}
